import { 
  collection, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  addDoc,
  serverTimestamp,
  Timestamp,
  increment,
  limit as firestoreLimit,
  deleteField,
  getCountFromServer
} from 'firebase/firestore';
import { db } from './config';
import { cache } from '../utils/cacheManager';

export const PROGRAM_COLLECTIONS = {
  PROGRAMS: 'programs',
  PROGRAM_REGISTRATIONS: 'programRegistrations', // Legacy - for backward compatibility
  PROGRAM_CATEGORIES: 'programCategories',
  EVENTS: 'events'
};

export interface Program {
  registrationDeadline: Timestamp | null; // Change from Date to Timestamp
  startTime?: string | null; // Add proper type
  endTime?: string | null; // Add proper type
  id?: string;
  title: string;
  description: string;
  content: string;
  category: string;
  location: string;
  organizer: string;
  capacity?: number;
  registeredCount: number;
  status: 'draft' | 'published' | 'ongoing' | 'completed' | 'cancelled';
  imageUrl?: string;
  newsCardImageUrl?: string;
  youtubeUrl?: string;
  tags?: string[];
  showInCarousel?: boolean;
  showInCards?: boolean;
  isFeatured?: boolean;
  isDeleted?: number; // 0 = active, 1 = deleted (soft-delete)
  requiresRegistration: boolean;
  newsCardImagePositionX?: number; // focal point for home news cards crop
  newsCardImagePositionY?: number; // focal point for home news cards crop
  requireGhanaCard?: boolean; // whether registration requires Ghana card
  requireDateOfBirth?: boolean; // whether registration requires date of birth
  
  // Program Dates
  startDate: Timestamp;
  endDate: Timestamp;
  
  // Registration Dates
  registrationStart?: Timestamp;
  registrationEnd?: Timestamp;
  
  // Published status
  isPublished: boolean;
  publishedAt?: Timestamp;
  publishedBy?: string;
  
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
}

export interface ProgramRegistration {
  id?: string;
  programId: string;
  programTitle: string;
  
  // ID fields
  idNumber?: string;
  idType?: string;
  
  // User details
  fullName: string;
  gender: 'male' | 'female';
  religion?: 'christian' | 'muslim' | 'other' | string;
  contactNumber: string;
  houseNumber?: string;
  address: string;
  email: string;
  
  // Registration details
  status: 'pending' | 'confirmed' | 'cancelled' | 'waitlisted';
  registrationDate: Timestamp;
  registrationNotes?: string;
  
  // Additional info
  emergencyContact?: string;
  specialRequirements?: string;
  attended?: boolean;
  attendanceDate?: Timestamp;
  
  // User tracking
  userId?: string;
  userEmail?: string;
  
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// New interface for Program Beneficiary (simplified for subcollection)
export interface ProgramBeneficiary {
  registrationNotes: string | undefined;
  id?: string;
  beneficiaryId: string; // Unique ID for the beneficiary
  fullName: string;
  gender: 'male' | 'female';
  contactNumber: string;
  houseNumber?: string;
  address: string;
  email: string;
  
  // Identification
  idNumber?: string;
  idType?: string;
  
  // Additional info
  religion?: 'christian' | 'muslim' | 'other' | string;
  age?: number;
  maritalStatus?: string;
  healthStatus?: string;
  emergencyContact?: string;
  specialRequirements?: string;
  
  // Registration info
  registrationStatus: 'pending' | 'confirmed' | 'cancelled' | 'waitlisted';
  registrationDate: Timestamp;
  attended?: boolean;
  attendanceDate?: Timestamp;
  dateOfBirth?: Timestamp;
  
  // Tracking
  registeredBy?: string; // User ID who registered this beneficiary
  registeredByEmail?: string;
  
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// New interface for Published Programs
export interface PublishedProgram {
  id?: string;
  programId: string;
  registrationFormEnabled: boolean;
  publishedBy: string;
  publishedAt: Timestamp;
  registrationStart: Timestamp;
  registrationEnd: Timestamp;
  isActive: boolean;
}

export interface ProgramEvent {
  id?: string;
  title: string;
  eventDate: Timestamp;
  endDate?: Timestamp;
  venue: string;
  organizer: string;
  category: string;
  attendeeName: string;
  attendeePhone?: string;
  attendeeEmail?: string;
  amount: number;
  paymentMethod: string;
  notes?: string;
  receiptNumber: string;
  accountingRecordId?: string;
  status: 'planned' | 'completed' | 'cancelled';
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
}

export interface ProgramStatsSummary {
  id?: string;
  programId: string;
  programTitle?: string;
  totalRegistrations?: number;
  confirmed?: number;
  pending?: number;
  cancelled?: number;
  waitlisted?: number;
  attended?: number;
  male?: number;
  female?: number;
  otherGender?: number;
  christian?: number;
  muslim?: number;
  otherReligion?: number;
  statusCounts?: Record<string, number>;
  genderCounts?: Record<string, number>;
  religionCounts?: Record<string, number>;
  maritalStatusCounts?: Record<string, number>;
  healthStatusCounts?: Record<string, number>;
  monthlyRegistrations?: Record<string, number>;
  recentRegistrations?: ProgramRegistration[];
  lastRegistrationAt?: Timestamp | null;
  lastUpdated?: Timestamp;
}

export interface ProgramMonthlyStatsSummary {
  id?: string;
  programId: string;
  programTitle?: string;
  month: string;
  totalRegistrations?: number;
  attended?: number;
  statusCounts?: Record<string, number>;
  genderCounts?: Record<string, number>;
  religionCounts?: Record<string, number>;
  maritalStatusCounts?: Record<string, number>;
  healthStatusCounts?: Record<string, number>;
  lastUpdated?: Timestamp;
}

// Helper function to get program registrations subcollection reference
const getProgramRegistrationsRef = (programId: string) => {
  return collection(db, 'programs', programId, 'registrations');
};

const getProgramStatsRef = (programId: string) => {
  return doc(db, 'programStats', programId);
};

const getProgramStatsCollectionRef = () => {
  return collection(db, 'programStats');
};

const getProgramMonthlyStatsCollectionRef = () => {
  return collection(db, 'programMonthlyStats');
};

// Helper function to get legacy program registrations reference
const getLegacyRegistrationsRef = () => {
  return collection(db, PROGRAM_COLLECTIONS.PROGRAM_REGISTRATIONS);
};

const getEventsRef = () => {
  return collection(db, PROGRAM_COLLECTIONS.EVENTS);
};

const normalizeBeneficiaryDataForStorage = <T extends Record<string, any>>(beneficiaryData: T): T => {
  const normalized = { ...beneficiaryData } as T & { dateOfBirth?: unknown; age?: unknown };
  const hasDateOfBirth = Object.prototype.hasOwnProperty.call(normalized, 'dateOfBirth');

  // Prefer storing full DOB; drop numeric age when DOB exists.
  if (hasDateOfBirth && normalized.dateOfBirth) {
    delete normalized.age;
  }

  return normalized as T;
};

// Add these functions to programFirestoreService.ts

// Get program statistics for dashboard
export const getProgramDashboardStats = async (): Promise<{
  success: boolean;
  data?: {
    totalPrograms: number;
    publishedPrograms: number;
    ongoingPrograms: number;
    upcomingPrograms: number;
    completedPrograms: number;
    totalRegistrations: number;
    totalAttended: number;
    averageAttendanceRate: number;
    programByCategory: Record<string, number>;
    monthlyRegistrations: Array<{ month: string; count: number }>;
    topPrograms: Array<{ id: string; title: string; registrations: number; attendanceRate: number }>;
    recentRegistrations: ProgramBeneficiary[];
  };
  error?: string;
}> => {
  try {
    // Get all programs
    const programs = await programService.getPrograms();
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Calculate statistics
    const stats = {
      totalPrograms: programs.length,
      publishedPrograms: programs.filter(p => p.isPublished).length,
      ongoingPrograms: programs.filter(p => {
        const startDate = p.startDate?.toDate();
        const endDate = p.endDate?.toDate();
        return p.status === 'ongoing' || 
               (startDate && endDate && now >= startDate && now <= endDate);
      }).length,
      upcomingPrograms: programs.filter(p => {
        const startDate = p.startDate?.toDate();
        return startDate && startDate > now && p.isPublished;
      }).length,
      completedPrograms: programs.filter(p => p.status === 'completed').length,
      totalRegistrations: 0,
      totalAttended: 0,
      averageAttendanceRate: 0,
      programByCategory: {} as Record<string, number>,
      monthlyRegistrations: [] as Array<{ month: string; count: number }>,
      topPrograms: [] as Array<{ id: string; title: string; registrations: number; attendanceRate: number }>,
      recentRegistrations: [] as ProgramBeneficiary[]
    };
    
    // Get program by category counts
    programs.forEach(program => {
      const category = program.category || 'Uncategorized';
      stats.programByCategory[category] = (stats.programByCategory[category] || 0) + 1;
    });
    
    // Prepare monthly buckets for the last 6 months (will include admin/manual registrations)
    const monthlyData: Record<string, number> = {};
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - i, 1);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      monthlyData[monthKey] = 0;
    }

    // Get registration statistics for each program and gather registrations
    const programPromises = programs.map(async (program) => {
      if (!program.id) return;

      const programStats = await programService.getProgramStats(program.id);
      if (programStats) {
        stats.totalRegistrations += programStats.total;
        stats.totalAttended += programStats.attended;

        const attendanceRate = programStats.total > 0
          ? Math.round((programStats.attended / programStats.total) * 100)
          : 0;

        if (programStats.total > 0) {
          stats.topPrograms.push({
            id: program.id,
            title: program.title,
            registrations: programStats.total,
            attendanceRate
          });
        }

        // Fetch only a small recent sample; counts above come from aggregate queries.
        const registrations = await programService.getProgramRegistrations(program.id, 10);

        if (registrations && registrations.length > 0) {
          // Add recent registrations (keep total up to 10)
          if (stats.recentRegistrations.length < 10) {
            const recent = registrations
              .slice()
              .sort((a, b) => {
                const dateA = a.registrationDate?.toDate?.() || new Date();
                const dateB = b.registrationDate?.toDate?.() || new Date();
                return dateB.getTime() - dateA.getTime();
              })
              .slice(0, 5);
            stats.recentRegistrations.push(...recent);
          }

          // Update monthly buckets using all registrations (this will capture admin/manual ones)
          registrations.forEach((reg) => {
            const regDate = reg.registrationDate?.toDate?.();
            if (regDate) {
              const monthKey = regDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
              if (monthlyData[monthKey] !== undefined) {
                monthlyData[monthKey]++;
              }
            }
          });
        }
      }
    });

    await Promise.all(programPromises);

    // Sort and trim top programs and recent registrations
    stats.topPrograms = stats.topPrograms.sort((a, b) => b.registrations - a.registrations).slice(0, 5);
    stats.recentRegistrations = stats.recentRegistrations
      .sort((a, b) => {
        const dateA = a.registrationDate?.toDate?.() || new Date();
        const dateB = b.registrationDate?.toDate?.() || new Date();
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 10);

    // Calculate average attendance rate
    if (stats.totalRegistrations > 0) {
      stats.averageAttendanceRate = Math.round((stats.totalAttended / stats.totalRegistrations) * 100);
    }

    // Convert monthlyData to array for response
    stats.monthlyRegistrations = Object.entries(monthlyData).map(([month, count]) => ({ month, count }));
    
    return { success: true, data: stats };
  } catch (error: any) {
    console.error('Error getting program dashboard stats:', error);
    return { success: false, error: error.message };
  }
};

// Get program performance trends
export const getProgramTrends = async (period: 'weekly' | 'monthly' | 'yearly' = 'monthly'): Promise<{
  success: boolean;
  data?: Array<{
    period: string;
    programs: number;
    registrations: number;
    attendanceRate: number;
    revenue?: number; // For future use if you add program fees
  }>;
  error?: string;
}> => {
  try {
    const programs = await programService.getPrograms();
    const now = new Date();
    
    let data: Array<{
      period: string;
      programs: number;
      registrations: number;
      attendanceRate: number;
    }> = [];
    
    switch (period) {
      case 'weekly':
        // Last 8 weeks
        for (let i = 7; i >= 0; i--) {
          const weekStart = new Date(now);
          weekStart.setDate(weekStart.getDate() - (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 7);
          
          const weekPrograms = programs.filter(p => {
            const startDate = p.startDate?.toDate();
            return startDate && startDate >= weekStart && startDate < weekEnd;
          });
          
          data.push({
            period: `Week ${i + 1}`,
            programs: weekPrograms.length,
            registrations: weekPrograms.reduce((sum, p) => sum + (p.registeredCount || 0), 0),
            attendanceRate: weekPrograms.length > 0 ? 75 : 0 // Simplified for now
          });
        }
        break;
        
      case 'monthly':
        // Last 6 months
        for (let i = 5; i >= 0; i--) {
          const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
          
          const monthPrograms = programs.filter(p => {
            const startDate = p.startDate?.toDate();
            return startDate && startDate >= month && startDate < nextMonth;
          });
          
          // Get total registrations for these programs
          let totalRegistrations = 0;
          let totalAttended = 0;
          
          for (const program of monthPrograms) {
            if (program.id) {
              const stats = await programService.getProgramStats(program.id);
              if (stats) {
                totalRegistrations += stats.total;
                totalAttended += stats.attended;
              }
            }
          }
          
          const attendanceRate = totalRegistrations > 0 
            ? Math.round((totalAttended / totalRegistrations) * 100) 
            : 0;
          
          data.push({
            period: month.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
            programs: monthPrograms.length,
            registrations: totalRegistrations,
            attendanceRate
          });
        }
        break;
        
      case 'yearly':
        // Last 3 years
        for (let i = 2; i >= 0; i--) {
          const year = new Date(now.getFullYear() - i, 0, 1);
          const nextYear = new Date(year.getFullYear() + 1, 0, 1);
          
          const yearPrograms = programs.filter(p => {
            const startDate = p.startDate?.toDate();
            return startDate && startDate >= year && startDate < nextYear;
          });
          
          data.push({
            period: year.getFullYear().toString(),
            programs: yearPrograms.length,
            registrations: yearPrograms.reduce((sum, p) => sum + (p.registeredCount || 0), 0),
            attendanceRate: 80 // Simplified
          });
        }
        break;
    }
    
    return { success: true, data };
  } catch (error: any) {
    console.error('Error getting program trends:', error);
    return { success: false, error: error.message };
  }
};

const invalidateProgramCaches = (programId?: string) => {
  try { cache.invalidate('programDashboardStats_v1'); } catch (e) {}
  try { cache.invalidatePattern(/^programDashboard:/); } catch (e) {}
  try { cache.invalidatePattern(/^programTrends:/); } catch (e) {}
  try { cache.invalidate('home.programs.published.10'); } catch (e) {}
  try { cache.invalidatePattern(/^home\.programs\.published\./); } catch (e) {}
  try { cache.invalidatePattern(/^home\.programs\.visible\./); } catch (e) {}
  try { cache.invalidatePattern(/^programs\.published/); } catch (e) {}
  if (programId) {
    try { cache.invalidate(`program.${programId}`); } catch (e) {}
  }
};

// CRUD Operations
export const programService = {
  // Event receipts
  async createEvent(eventData: Omit<ProgramEvent, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(getEventsRef(), {
      ...eventData,
      amount: Number(eventData.amount) || 0,
      status: eventData.status || 'planned',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return docRef.id;
  },

  async getEvents(limitCount = 75): Promise<ProgramEvent[]> {
    try {
      const q = query(
        getEventsRef(),
        orderBy('eventDate', 'desc'),
        firestoreLimit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as ProgramEvent) }));
    } catch (error) {
      console.error('Error fetching events:', error);
      return [];
    }
  },

  async updateEvent(eventId: string, data: Partial<ProgramEvent>) {
    try {
      const eventRef = doc(db, PROGRAM_COLLECTIONS.EVENTS, eventId);
      const updatePayload: Record<string, any> = {
        ...data,
        updatedAt: serverTimestamp()
      };
      if (data.amount !== undefined) {
        updatePayload.amount = Number(data.amount) || 0;
      }
      await updateDoc(eventRef, {
        ...updatePayload
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, error };
    }
  },

  async deleteEvent(eventId: string) {
    try {
      await deleteDoc(doc(db, PROGRAM_COLLECTIONS.EVENTS, eventId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { success: false, error };
    }
  },

  // Program CRUD
  async createProgram(programData: Omit<Program, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const programRef = collection(db, PROGRAM_COLLECTIONS.PROGRAMS);
    const newProgram = {
      ...programData,
      isDeleted: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      registeredCount: 0,
      isPublished: false,
      status: 'draft' as const,
      showInCarousel: !!(programData as any).showInCarousel,
      showInCards: !!(programData as any).showInCards,
      requireGhanaCard: !!(programData as any).requireGhanaCard,
      requireDateOfBirth: !!(programData as any).requireDateOfBirth
    };
    
    console.log('Creating program with status:', newProgram.status, 'isPublished:', newProgram.isPublished);
    const docRef = await addDoc(programRef, newProgram);
    try {
      invalidateProgramCaches();
    } catch (e) {}

    return docRef.id;
  },

  async getPrograms(limit?: number, status?: string[]) {
    // Exclude soft-deleted programs (isDeleted === 1)
    // Build query to only include programs where isDeleted is 0 (active)
    let q = query(
      collection(db, PROGRAM_COLLECTIONS.PROGRAMS),
      where('isDeleted', '==', 0),
      orderBy('createdAt', 'desc')
    );

    if (status && status.length > 0) {
      q = query(q, where('status', 'in', status));
    }

    if (limit) {
      q = query(q, firestoreLimit(limit));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Program));
  },

  async getPublishedPrograms(limitCount = 50): Promise<Program[]> {
    try {
      const programsRef = collection(db, PROGRAM_COLLECTIONS.PROGRAMS);
      // Exclude soft-deleted programs
      const baseFilters: any[] = [where('isPublished', '==', true), where('isDeleted', '==', 0), where('status', 'in', ['published', 'ongoing'])];
      const q = limitCount
        ? query(programsRef, ...baseFilters, orderBy('startDate', 'desc'), firestoreLimit(limitCount))
        : query(programsRef, ...baseFilters, orderBy('startDate', 'desc'));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...(d.data() as Program) }));
    } catch (err) {
      console.error('Error fetching published programs (optimized):', err);
      return [];
    }
  },

  async getHomepageVisiblePrograms(limitCount = 16): Promise<Program[]> {
    try {
      const programsRef = collection(db, PROGRAM_COLLECTIONS.PROGRAMS);
      const perSectionLimit = Math.max(1, limitCount);
      const fallbackLimit = Math.max(perSectionLimit, 100);

      const runHomepageQuery = async (visibilityField: 'showInCards' | 'showInCarousel') => {
        const baseFilters = [
          where('isPublished', '==', true),
          where('isDeleted', '==', 0),
          where(visibilityField, '==', true)
        ];

        try {
          return await getDocs(query(
            programsRef,
            ...baseFilters,
            orderBy('startDate', 'desc'),
            firestoreLimit(perSectionLimit)
          ));
        } catch (error: any) {
          const message = String(error?.message || error?.details || '');
          if (error?.code !== 'failed-precondition' && error?.code !== 9 && !message.includes('requires an index')) {
            throw error;
          }

          console.warn(`Homepage programs ${visibilityField} query is missing an index; using unordered fallback.`, error);
          return await getDocs(query(
            programsRef,
            ...baseFilters,
            firestoreLimit(fallbackLimit)
          ));
        }
      };

      const [cardsSnapshot, carouselSnapshot] = await Promise.all([
        runHomepageQuery('showInCards'),
        runHomepageQuery('showInCarousel')
      ]);

      const byId = new Map<string, Program>();
      [...cardsSnapshot.docs, ...carouselSnapshot.docs].forEach(d => {
        byId.set(d.id, { id: d.id, ...(d.data() as Program) });
      });

      return Array.from(byId.values())
        .sort((a, b) => {
          const aStart = a.startDate?.toMillis?.() || a.startDate?.toDate?.()?.getTime?.() || 0;
          const bStart = b.startDate?.toMillis?.() || b.startDate?.toDate?.()?.getTime?.() || 0;
          return bStart - aStart;
        })
        .slice(0, limitCount);
    } catch (err) {
      console.error('Error fetching homepage visible programs:', err);
      return [];
    }
  },

  async publishProgram(programId: string, registrationData: {
    registrationStart: Timestamp;
    registrationEnd: Timestamp;
  }) {
    const programRef = doc(db, PROGRAM_COLLECTIONS.PROGRAMS, programId);
    
    const programSnap = await getDoc(programRef);
    if (!programSnap.exists()) {
      throw new Error('Program not found');
    }
    
    console.log('Current program before publish:', programSnap.data());
    
    await updateDoc(programRef, {
      isPublished: true,
      publishedAt: serverTimestamp(),
      status: 'published',
      registrationStart: registrationData.registrationStart,
      registrationEnd: registrationData.registrationEnd,
      updatedAt: serverTimestamp()
    });
    
    const updatedSnap = await getDoc(programRef);
    console.log('Program after publish:', updatedSnap.data());
    console.log(`Program ${programId} published successfully`);
    invalidateProgramCaches(programId);
  },

  async getProgram(programId: string): Promise<Program | null> {
    try {
      const programRef = doc(db, PROGRAM_COLLECTIONS.PROGRAMS, programId);
      const snap = await getDoc(programRef);
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Program) };
    } catch (error) {
      console.error('Error fetching program:', error);
      return null;
    }
  },

  async updateProgram(programId: string, data: Partial<Program>) {
    try {
      const programRef = doc(db, PROGRAM_COLLECTIONS.PROGRAMS, programId);
      await updateDoc(programRef, { ...data, updatedAt: serverTimestamp() });
      invalidateProgramCaches(programId);
      return { success: true };
    } catch (error) {
      console.error('Error updating program:', error);
      return { success: false, error };
    }
  },

  // Soft-delete a program (mark isDeleted = 1)
  async softDeleteProgram(programId: string) {
    try {
      const programRef = doc(db, PROGRAM_COLLECTIONS.PROGRAMS, programId);
      await updateDoc(programRef, { isDeleted: 1, updatedAt: serverTimestamp() });
      invalidateProgramCaches(programId);
      return { success: true };
    } catch (error) {
      console.error('Error soft-deleting program:', error);
      return { success: false, error };
    }
  },

  // Get program registrations from subcollection
  async getProgramRegistrations(programId: string, limitCount?: number): Promise<ProgramBeneficiary[]> {
    try {
      const registrationsRef = getProgramRegistrationsRef(programId);
      const constraints: any[] = [orderBy('registrationDate', 'desc')];
      if (limitCount) constraints.push(firestoreLimit(limitCount));
      const q = query(registrationsRef, ...constraints);
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      } as ProgramBeneficiary));
    } catch (error) {
      console.error('Error fetching program registrations:', error);
      return [];
    }
  },

  // Get legacy program registrations (for backward compatibility)
  async getLegacyProgramRegistrations(programId: string): Promise<ProgramRegistration[]> {
    const q = query(
      getLegacyRegistrationsRef(),
      where('programId', '==', programId),
      orderBy('registrationDate', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ProgramRegistration));
  },

  // Register for program (new structure - adds to subcollection)
  async registerForProgram(programId: string, beneficiaryData: Omit<ProgramBeneficiary, 'id' | 'registrationDate' | 'createdAt' | 'updatedAt'>) {
    try {
      // Generate a unique beneficiary ID
      const beneficiaryId = `BEN-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const normalizedBeneficiaryData = normalizeBeneficiaryDataForStorage(beneficiaryData as any);
      
      const registrationRef = getProgramRegistrationsRef(programId);
      const newRegistration = {
        ...normalizedBeneficiaryData,
        beneficiaryId,
        programId,
        registrationDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Add to program's registrations subcollection
      const docRef = await addDoc(registrationRef, newRegistration);
      
      // Update program count as best effort.
      // Public users may not have permission to update parent program docs.
      try {
        const programRef = doc(db, PROGRAM_COLLECTIONS.PROGRAMS, programId);
        await updateDoc(programRef, {
          registeredCount: increment(1),
          updatedAt: serverTimestamp()
        });
      } catch (countUpdateError) {
        console.warn('Registered count update skipped (permission or race condition):', countUpdateError);
      }
      
      // Also add to legacy collection for backward compatibility
      await this.addToLegacyRegistrations(programId, normalizedBeneficiaryData, docRef.id);

      // Invalidate dashboard caches after new registration
      try { cache.invalidate('programDashboardStats_v1'); } catch (e) {}
      try { cache.invalidatePattern(/^programDashboard:/); } catch (e) {}
      try { cache.invalidatePattern(/^programTrends:/); } catch (e) {}

      return { 
        success: true, 
        registrationId: docRef.id,
        beneficiaryId 
      };
    } catch (error) {
      console.error('Error registering for program:', error);
      return { success: false, error };
    }
  },

  // Legacy registration method (for backward compatibility)
  async registerForProgramLegacy(registrationData: Omit<ProgramRegistration, 'id' | 'registrationDate'>) {
    const registrationRef = getLegacyRegistrationsRef();
    const newRegistration = {
      ...registrationData,
      registrationDate: serverTimestamp(),
      status: 'pending' as const,
      createdAt: serverTimestamp()
    };
    
    // Update program's registered count
    const programRef = doc(db, PROGRAM_COLLECTIONS.PROGRAMS, registrationData.programId);
    const programSnap = await getDoc(programRef);
    
    if (programSnap.exists()) {
      const program = programSnap.data() as Program;
      await updateDoc(programRef, {
        registeredCount: (program.registeredCount || 0) + 1
      });
    }
    
    const docRef = await addDoc(registrationRef, newRegistration);
    try { cache.invalidate('programDashboardStats_v1'); } catch (e) {}
    try { cache.invalidatePattern(/^programDashboard:/); } catch (e) {}
    try { cache.invalidatePattern(/^programTrends:/); } catch (e) {}
    return docRef;
  },

  // Helper: Add to legacy registrations collection
  async addToLegacyRegistrations(programId: string, beneficiaryData: any, registrationId: string) {
    try {
      const normalizedBeneficiaryData = normalizeBeneficiaryDataForStorage(beneficiaryData);
      const hasDateOfBirth = !!normalizedBeneficiaryData.dateOfBirth;
      const legacyRef = getLegacyRegistrationsRef();
      const legacyRegistration = {
        programId,
        programTitle: normalizedBeneficiaryData.programTitle || '',
        idNumber: normalizedBeneficiaryData.idNumber,
        idType: normalizedBeneficiaryData.idType,
        fullName: normalizedBeneficiaryData.fullName,
        gender: normalizedBeneficiaryData.gender,
        religion: normalizedBeneficiaryData.religion,
        contactNumber: normalizedBeneficiaryData.contactNumber,
        houseNumber: normalizedBeneficiaryData.houseNumber,
        address: normalizedBeneficiaryData.address,
        email: normalizedBeneficiaryData.email,
        dateOfBirth: normalizedBeneficiaryData.dateOfBirth || null,
        age: hasDateOfBirth ? undefined : normalizedBeneficiaryData.age,
        maritalStatus: normalizedBeneficiaryData.maritalStatus,
        healthStatus: normalizedBeneficiaryData.healthStatus,
        status: normalizedBeneficiaryData.registrationStatus || 'confirmed',
        registrationDate: serverTimestamp(),
        specialRequirements: normalizedBeneficiaryData.specialRequirements,
        emergencyContact: normalizedBeneficiaryData.emergencyContact,
        userId: normalizedBeneficiaryData.registeredBy,
        userEmail: normalizedBeneficiaryData.registeredByEmail,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await addDoc(legacyRef, legacyRegistration);
      try { cache.invalidate('programDashboardStats_v1'); } catch (e) {}
      try { cache.invalidatePattern(/^programDashboard:/); } catch (e) {}
      try { cache.invalidatePattern(/^programTrends:/); } catch (e) {}
    } catch (error) {
      console.warn('Legacy registration mirror skipped:', error);
    }
  },

  // Get beneficiary by ID
  async getBeneficiary(programId: string, beneficiaryId: string): Promise<ProgramBeneficiary | null> {
    try {
      const registrationsRef = getProgramRegistrationsRef(programId);
      const q = query(registrationsRef, where('beneficiaryId', '==', beneficiaryId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) return null;
      
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as ProgramBeneficiary;
    } catch (error) {
      console.error('Error fetching beneficiary:', error);
      return null;
    }
  },

  // Update beneficiary
  async updateBeneficiary(programId: string, beneficiaryId: string, data: Partial<ProgramBeneficiary>) {
    try {
      // First find the document by beneficiaryId
      const registrationsRef = getProgramRegistrationsRef(programId);
      const q = query(registrationsRef, where('beneficiaryId', '==', beneficiaryId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return { success: false, error: 'Beneficiary not found' };
      }
      
      const docId = snapshot.docs[0].id;
      const beneficiaryRef = doc(db, 'programs', programId, 'registrations', docId);
      const normalizedData = normalizeBeneficiaryDataForStorage(data as any);
      const hasDateOfBirth = Object.prototype.hasOwnProperty.call(normalizedData, 'dateOfBirth');
      const updatePayload: Record<string, any> = {
        ...normalizedData,
        updatedAt: serverTimestamp()
      };

      // If DOB is being written/updated, remove stale numeric age from older records.
      if (hasDateOfBirth) {
        updatePayload.age = deleteField();
      }
      
      await updateDoc(beneficiaryRef, updatePayload);
      try { cache.invalidate('programDashboardStats_v1'); } catch (e) {}
      try { cache.invalidatePattern(/^programDashboard:/); } catch (e) {}
      try { cache.invalidatePattern(/^programTrends:/); } catch (e) {}
      return { success: true };
    } catch (error) {
      console.error('Error updating beneficiary:', error);
      return { success: false, error };
    }
  },

  // Mark beneficiary attendance
  async markAttendance(programId: string, beneficiaryId: string) {
    try {
      const registrationsRef = getProgramRegistrationsRef(programId);
      const q = query(registrationsRef, where('beneficiaryId', '==', beneficiaryId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return { success: false, error: 'Beneficiary not found' };
      }
      
      const docId = snapshot.docs[0].id;
      const beneficiaryRef = doc(db, 'programs', programId, 'registrations', docId);
      
      await updateDoc(beneficiaryRef, { 
        attended: true,
        attendanceDate: serverTimestamp(),
        registrationStatus: 'confirmed',
        status: 'confirmed',
        updatedAt: serverTimestamp()
      });
      try { cache.invalidate('programDashboardStats_v1'); } catch (e) {}
      try { cache.invalidatePattern(/^programDashboard:/); } catch (e) {}
      try { cache.invalidatePattern(/^programTrends:/); } catch (e) {}
      return { success: true };
    } catch (error) {
      console.error('Error marking attendance:', error);
      return { success: false, error };
    }
  },

  // Check if ID number already exists in program
  async checkDuplicateId(programId: string, idNumber: string): Promise<boolean> {
    try {
      const registrationsRef = getProgramRegistrationsRef(programId);
      const q = query(registrationsRef, where('idNumber', '==', idNumber));
      const snapshot = await getDocs(q);
      
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking duplicate ID:', error);
      return false;
    }
  },

  async getProgramStatsSummary(programId: string): Promise<ProgramStatsSummary | null> {
    try {
      const snap = await getDoc(getProgramStatsRef(programId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Omit<ProgramStatsSummary, 'id'>) };
    } catch (error) {
      console.error('Error fetching program stats summary:', error);
      return null;
    }
  },

  async getProgramStatsSummaries(limitCount = 200): Promise<ProgramStatsSummary[]> {
    try {
      const q = query(getProgramStatsCollectionRef(), firestoreLimit(limitCount));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as Omit<ProgramStatsSummary, 'id'>) }));
    } catch (error) {
      console.error('Error fetching program stats summaries:', error);
      return [];
    }
  },

  async getProgramMonthlyStatsSummaries(programId?: string, limitCount = 24): Promise<ProgramMonthlyStatsSummary[]> {
    try {
      const constraints: any[] = [];
      if (programId) constraints.push(where('programId', '==', programId));
      constraints.push(orderBy('month', 'desc'), firestoreLimit(limitCount));
      const q = query(getProgramMonthlyStatsCollectionRef(), ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as Omit<ProgramMonthlyStatsSummary, 'id'>) }));
    } catch (error) {
      console.error('Error fetching program monthly stats summaries:', error);
      return [];
    }
  },

  // Get program statistics
  async getProgramStats(programId: string) {
    try {
      const registrationsRef = getProgramRegistrationsRef(programId);
      const [
        totalSnap,
        confirmedSnap,
        pendingSnap,
        cancelledSnap,
        attendedSnap,
        maleSnap,
        femaleSnap
      ] = await Promise.all([
        getCountFromServer(registrationsRef),
        getCountFromServer(query(registrationsRef, where('registrationStatus', '==', 'confirmed'))),
        getCountFromServer(query(registrationsRef, where('registrationStatus', '==', 'pending'))),
        getCountFromServer(query(registrationsRef, where('registrationStatus', '==', 'cancelled'))),
        getCountFromServer(query(registrationsRef, where('attended', '==', true))),
        getCountFromServer(query(registrationsRef, where('gender', '==', 'male'))),
        getCountFromServer(query(registrationsRef, where('gender', '==', 'female')))
      ]);
      
      return {
        total: totalSnap.data().count,
        confirmed: confirmedSnap.data().count,
        pending: pendingSnap.data().count,
        cancelled: cancelledSnap.data().count,
        attended: attendedSnap.data().count,
        male: maleSnap.data().count,
        female: femaleSnap.data().count
      };
    } catch (error) {
      console.error('Error getting program stats:', error);
      return null;
    }
  }
};

// Debug functions
export const debugPrograms = {
  async getAllProgramsWithDetails() {
    try {
      const programsRef = collection(db, PROGRAM_COLLECTIONS.PROGRAMS);
      const snapshot = await getDocs(programsRef);
      const programs = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          isPublished: data.isPublished,
          status: data.status,
          startDate: data.startDate?.toDate()?.toISOString(),
          registrationStart: data.registrationStart?.toDate()?.toISOString(),
          registrationEnd: data.registrationEnd?.toDate()?.toISOString(),
          createdAt: data.createdAt?.toDate()?.toISOString(),
          updatedAt: data.updatedAt?.toDate()?.toISOString(),
          registeredCount: data.registeredCount || 0
        };
      });
      
      console.table(programs);
      return programs;
    } catch (error) {
      console.error('Debug error:', error);
      return [];
    }
  },

  async getProgramRegistrationsCount(programId: string) {
    try {
      const registrationsRef = getProgramRegistrationsRef(programId);
      const snapshot = await getDocs(registrationsRef);
      console.log(`Program ${programId} has ${snapshot.size} registrations`);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting registrations count:', error);
      return 0;
    }
  }
};

// Increment program views (simple atomic increment)
export const incrementProgramViews = async (programId: string) => {
  try {
    const programRef = doc(db, PROGRAM_COLLECTIONS.PROGRAMS, programId);
    await updateDoc(programRef, {
      views: increment(1),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error: any) {
    if (error?.code === 'permission-denied') {
      console.warn('Program views increment skipped due to permissions.');
      return { success: false, error: 'permission-denied' };
    }
    console.error('Error incrementing program views:', error);
    return { success: false, error: error.message };
  }
};
